import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdDashboard, 
  MdSchedule, 
  MdPeople,
  MdBarChart,
  MdAdd,
  MdList,
  MdSchool,
  MdTrendingUp
} from 'react-icons/md';

/**
 * Faculty Admin Dashboard Component
 * Shows faculty-wide statistics and admin features
 */
function FacultyAdminDashboard() {
  const navigate = useNavigate();
  const { user, getToken } = useAttendanceAuth();
  const [dashboard, setDashboard] = useState({
    batch_count: 0,
    timetable_count: 0,
    active_timetable_count: 0,
    draft_timetable_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [facultiesLoaded, setFacultiesLoaded] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const extractArray = (result, keys = []) => {
    if (Array.isArray(result)) {
      return result;
    }

    for (const key of keys) {
      if (Array.isArray(result?.[key])) {
        return result[key];
      }
    }

    if (Array.isArray(result?.data)) {
      return result.data;
    }

    if (Array.isArray(result?.records)) {
      return result.records;
    }

    return [];
  };

  const dedupeById = (items) => {
    const map = new Map();

    items.forEach((item, index) => {
      const key = item?.id ?? item?.batch_id ?? item?.timetable_id ?? `fallback-${index}`;
      if (!map.has(String(key))) {
        map.set(String(key), item);
      }
    });

    return Array.from(map.values());
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();

      const facultiesResult = await attendanceAPI.getFaculties(token).catch((err) => {
        console.error('Error fetching faculties:', err);
        return { success: false, faculties: [] };
      });

      const loadedFaculties = extractArray(facultiesResult, ['faculties']);

      let loadedBatches = [];
      let loadedTimetables = [];

      // Prefer unfiltered faculty-admin endpoints first.
      // In this project, they are often already scoped by the logged-in faculty admin.
      const [batchesResult, timetablesResult] = await Promise.all([
        attendanceAPI.getBatches({}, token).catch((err) => {
          console.error('Error fetching batches:', err);
          return { success: false, batches: [] };
        }),
        attendanceAPI.getTimetables({}, token).catch((err) => {
          console.error('Error fetching timetables:', err);
          return { success: false, timetables: [] };
        })
      ]);

      loadedBatches = dedupeById(extractArray(batchesResult, ['batches']));
      loadedTimetables = dedupeById(extractArray(timetablesResult, ['timetables']));

      // Fallback: if the unfiltered endpoints return nothing, try per-faculty fanout.
      // Some backends require faculty filters, but others crash on them.
      if (
        loadedFaculties.length > 0 &&
        loadedBatches.length === 0 &&
        loadedTimetables.length === 0
      ) {
        const perFacultyResults = await Promise.all(
          loadedFaculties.map(async (faculty) => {
            const facultyId = faculty?.id;

            if (!facultyId) {
              return { batches: [], timetables: [] };
            }

            const [facultyBatchesResult, facultyTimetablesResult] = await Promise.all([
              attendanceAPI.getBatches({ faculty_id: facultyId }, token).catch((err) => {
                console.error(`Error fetching batches for faculty ${facultyId}:`, err);
                return { success: false, batches: [] };
              }),
              attendanceAPI.getTimetables({ faculty_id: facultyId }, token).catch((err) => {
                console.error(`Error fetching timetables for faculty ${facultyId}:`, err);
                return { success: false, timetables: [] };
              })
            ]);

            return {
              batches: extractArray(facultyBatchesResult, ['batches']),
              timetables: extractArray(facultyTimetablesResult, ['timetables'])
            };
          })
        );

        loadedBatches = dedupeById(perFacultyResults.flatMap((result) => result.batches));
        loadedTimetables = dedupeById(perFacultyResults.flatMap((result) => result.timetables));
      }

      const activeTimetables = loadedTimetables.filter(
        (timetable) => (timetable?.state || '').toLowerCase() === 'active'
      ).length;

      const draftTimetables = loadedTimetables.filter(
        (timetable) => (timetable?.state || '').toLowerCase() === 'draft'
      ).length;

      setFaculties(loadedFaculties);
      setFacultiesLoaded(true);
      setDashboard({
        batch_count: loadedBatches.length,
        timetable_count: loadedTimetables.length,
        active_timetable_count: activeTimetables,
        draft_timetable_count: draftTimetables
      });

      if (
        loadedFaculties.length === 0 &&
        loadedBatches.length === 0 &&
        loadedTimetables.length === 0
      ) {
        setError('Failed to load faculty dashboard data');
      }
    } catch (err) {
      console.error('Error fetching faculty dashboard data:', err);
      setError(err.message || 'Failed to load dashboard');
      setFacultiesLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Create Timetable",
      description: "Create a new timetable for batches",
      icon: <MdAdd className="text-3xl" />,
      path: "/attendance/faculty-admin/timetable/create",
      color: "bg-blue-500",
      action: "primary"
    },
    {
      title: "View Timetables",
      description: "View and manage all timetables",
      icon: <MdList className="text-3xl" />,
      path: "/attendance/faculty-admin/timetables",
      color: "bg-green-500",
      action: "secondary"
    },
    {
      title: "Attendance Statistics",
      description: "View faculty-wide attendance stats",
      icon: <MdBarChart className="text-3xl" />,
      path: "/attendance/faculty-admin/attendance-stats",
      color: "bg-orange-500",
      action: "secondary"
    },
    {
      title: "View Attendance",
      description: "View all scheduled Attendance",
      icon: <MdSchedule className="text-3xl" />,
      path: "/attendance/faculty-admin/sessions",
      color: "bg-indigo-500",
      action: "secondary"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-red-800 font-semibold mb-1">Error Loading Dashboard</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Welcome, {user?.name || 'Faculty Admin'}
            </h1>
            <p className="text-gray-600">
              Faculty Admin Dashboard - Manage your {facultiesLoaded && faculties.length > 1 ? 'faculties' : 'faculty'}
            </p>
            {/* Show faculty name only if single faculty, otherwise show general title */}
            {/* Wait for faculties to load before deciding what to show */}
            {facultiesLoaded && faculties.length > 1 ? (
              <div className="mt-3 flex items-center space-x-2">
                <MdSchool className="text-blue-500" />
                <p className="text-lg font-semibold text-blue-600">
                  Multiple Faculties Administration
                </p>
              </div>
            ) : facultiesLoaded && faculties.length === 1 ? (
              <div className="mt-3 flex items-center space-x-2">
                <MdSchool className="text-blue-500" />
                <p className="text-lg font-semibold text-blue-600">
                  {faculties[0].name || faculties[0].code || dashboard?.faculty_name}
                </p>
              </div>
            ) : dashboard?.faculty_name && !facultiesLoaded ? (
              // Show dashboard faculty name only while loading, or if faculties failed to load
              <div className="mt-3 flex items-center space-x-2">
                <MdSchool className="text-blue-500" />
                <p className="text-lg font-semibold text-blue-600">
                  {dashboard.faculty_name}
                </p>
              </div>
            ) : null}
          </div>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center space-x-2"
            title="Refresh"
          >
            <MdTrendingUp />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-blue-100 text-sm mb-1">Total Batches</p>
              <p className="text-4xl font-bold">{dashboard?.batch_count || 0}</p>
            </div>
            <MdPeople className="text-5xl text-blue-200 opacity-80" />
          </div>
          <div className="pt-4 border-t border-blue-400">
            <p className="text-xs text-blue-100">Batches in faculty</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-green-100 text-sm mb-1">Total Timetables</p>
              <p className="text-4xl font-bold">{dashboard?.timetable_count || 0}</p>
            </div>
            <MdSchedule className="text-5xl text-green-200 opacity-80" />
          </div>
          <div className="pt-4 border-t border-green-400">
            <p className="text-xs text-green-100">All timetables</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-purple-100 text-sm mb-1">Active Timetables</p>
              <p className="text-4xl font-bold">{dashboard?.active_timetable_count || 0}</p>
            </div>
            <MdTrendingUp className="text-5xl text-purple-200 opacity-80" />
          </div>
          <div className="pt-4 border-t border-purple-400">
            <p className="text-xs text-purple-100">Currently active</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-orange-100 text-sm mb-1">Draft Timetables</p>
              <p className="text-4xl font-bold">{dashboard?.draft_timetable_count || 0}</p>
            </div>
            <MdSchedule className="text-5xl text-orange-200 opacity-80" />
          </div>
          <div className="pt-4 border-t border-orange-400">
            <p className="text-xs text-orange-100">Pending approval</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              className={`bg-white border-2 rounded-xl p-6 hover:shadow-xl transition-all text-left group transform hover:-translate-y-1 ${
                action.action === 'primary'
                  ? 'border-blue-500 hover:border-blue-600'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`${action.color} text-white rounded-lg p-4 w-16 h-16 flex items-center justify-center mb-4 group-hover:scale-110 transition shadow-lg`}>
                {action.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition">
                {action.title}
              </h3>
              <p className="text-sm text-gray-600">{action.description}</p>
              {action.action === 'primary' && (
                <div className="mt-4">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    Primary Action
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}

export default FacultyAdminDashboard;
